"use client";
import { motion, useMotionValue, useSpring, AnimatePresence, useInView, animate as frameAnimate } from "framer-motion";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { useWallet } from "@/context/WalletContext";
import { useTheme } from "@/context/ThemeContext";
import {
  Zap, BarChart3, Shield, TrendingUp, Brain, Globe2, ArrowRight, Wallet,
  Mic, RefreshCcw, Search, Eye, BookOpen, Scale, FileText, Swords, User, DollarSign,
  ChevronDown, Sun, Moon, Send, Database, Activity, Layers, Target,
  Network, Cpu, MessageSquare, Star, Check, Bolt, Radio,
  X, Code2,
} from "lucide-react";
import { API_URL } from '@/lib/env';
import { ProductionState } from '@/components/ProductionState';
import { useEffect, useRef, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import MCP from "@lobehub/icons/es/MCP";

// ── Constants (from landing-content.ts — aligned with SOSOMIND_DOCUMENTATION.md) ──

import {
  HERO_TAGLINE,
  HERO_SUB,
  NAV_LINKS,
  STATS,
  PARTNERS,
  FEATURES as FEATURES_RAW,
  AGENTS as AGENTS_RAW,
  DATA_SOURCES as DATA_SOURCES_RAW,
  FAQ_ITEMS,
  FOOTER_LINKS,
  EXECUTION_STEPS,
} from "@/content/landing-content";

const FEATURE_ICON_MAP: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  Brain: Brain,
  Zap: Zap,
  Shield: Shield,
  Wallet: Wallet,
  BarChart3: BarChart3,
  Globe2: Globe2,
  Layers: Layers,
  Radio: Radio,
  Send: Send,
  Activity: Activity,
  BookOpen: BookOpen,
  Cpu: Cpu,
  Network: Network,
  Bolt: Bolt,
};

const AGENTS = AGENTS_RAW.map((a) => ({
  ...a,
  icon: FEATURE_ICON_MAP[a.icon as string] ?? Network,
}));

const FEATURES = FEATURES_RAW.map((f) => ({
  ...f,
  icon: FEATURE_ICON_MAP[f.icon] ?? Zap,
}));

const DATA_SOURCE_ICON_MAP: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  Database: Database,
  Zap: Zap,
  Activity: Activity,
  TrendingUp: TrendingUp,
  Globe2: Globe2,
  DollarSign: DollarSign,
  MessageSquare: MessageSquare,
  Layers: Layers,
  BarChart3: BarChart3,
  BookOpen: BookOpen,
  Star: Star,
  FileText: FileText,
  Target: Target,
};

const DATA_SOURCES = DATA_SOURCES_RAW.map((ds, i) => ({
  name: ds.name,
  tools: ds.tools,
  type: ds.type.split(" + ")[0] === "REST" ? "REST" : ds.type.includes("MCP") ? "MCP" : ds.type.includes("Realtime") ? "Feed" : "Feed",
  icon: [Database, Zap, Activity, Globe2, DollarSign, Target, BookOpen, Star, FileText, TrendingUp, Layers, Activity][i] ?? Database,
}));

// ── Micro-components ──────────────────────────────────────────────────────────

function SplitHeadline({ text, className, style }: { text: string; className?: string; style?: React.CSSProperties }) {
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
  };
  const char = {
    hidden:  { opacity: 0, y: 28, filter: "blur(8px)" },
    visible: { opacity: 1, y: 0,  filter: "blur(0px)", transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] as [number,number,number,number] } },
  };
  const words = text.split(" ");
  return (
    <motion.h1 className={className} style={style} variants={container} initial="hidden" animate="visible">
      {words.map((word, wi) => (
        <span key={wi} style={{ display: "inline-block", whiteSpace: "nowrap" }}>
          {word.split("").map((c, ci) => (
            <motion.span key={ci} variants={char} style={{ display: "inline-block" }}>
              {c}
            </motion.span>
          ))}
          {wi < words.length - 1 && (
            <motion.span variants={char} style={{ display: "inline-block" }}>&nbsp;</motion.span>
          )}
        </span>
      ))}
    </motion.h1>
  );
}

function SpotlightCard({ children, className = "", style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const [pos, setPos] = useState({ x: 0, y: 0, opacity: 0 });
  return (
    <div
      className={`relative overflow-hidden rounded-[8px] border ${className}`}
      style={{ borderColor: "var(--glass-border)", background: "var(--bg-card)", ...style }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({ x: e.clientX - r.left, y: e.clientY - r.top, opacity: 1 });
      }}
      onMouseLeave={() => setPos((p) => ({ ...p, opacity: 0 }))}
    >
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-300"
        style={{ opacity: pos.opacity, background: `radial-gradient(400px circle at ${pos.x}px ${pos.y}px, rgba(249,115,22,0.1), transparent 50%)` }}
      />
      {children}
    </div>
  );
}

function MagneticButton({ children, onClick, disabled, className = "", style }: {
  children: React.ReactNode; onClick?: () => void; disabled?: boolean; className?: string; style?: React.CSSProperties;
}) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 200, damping: 15 });
  const sy = useSpring(my, { stiffness: 200, damping: 15 });
  return (
    <motion.button
      style={{ x: sx, y: sy, ...style }}
      className={className}
      disabled={disabled}
      onClick={onClick}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left - r.width / 2) * 0.3);
        my.set((e.clientY - r.top - r.height / 2) * 0.3);
      }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
    >
      {children}
    </motion.button>
  );
}

function SignalBadge({ dir }: { dir: string }) {
  const styleMap: Record<string, React.CSSProperties> = {
    LONG:    { background: "rgba(34,197,94,0.15)",  color: "#22c55e", borderColor: "rgba(34,197,94,0.3)"  },
    SHORT:   { background: "rgba(239,68,68,0.15)",  color: "#ef4444", borderColor: "rgba(239,68,68,0.3)"  },
    NEUTRAL: { background: "rgba(96,165,250,0.15)", color: "#60a5fa", borderColor: "rgba(96,165,250,0.3)" },
  };
  return <span className="px-2 py-0.5 rounded text-xs font-bold border" style={styleMap[dir]}>{dir}</span>;
}

function ImpactBadge({ impact }: { impact: string }) {
  const map: Record<string, React.CSSProperties> = {
    HIGH:   { background: "rgba(249,115,22,0.15)",  color: "#f97316", borderColor: "rgba(249,115,22,0.3)"  },
    MEDIUM: { background: "rgba(250,204,21,0.12)",  color: "#eab308", borderColor: "rgba(250,204,21,0.25)" },
    LOW:    { background: "rgba(107,114,128,0.15)", color: "#6b7280", borderColor: "rgba(107,114,128,0.25)"},
  };
  return <span className="px-2 py-0.5 rounded text-xs font-bold border" style={map[impact]}>{impact}</span>;
}

function AnimatedStat({ to, suffix, fmt }: { to: number; suffix: string; fmt: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  useEffect(() => {
    if (!isInView || !ref.current) return;
    const el = ref.current;
    const controls = frameAnimate(0, to, {
      duration: 2,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
      onUpdate(v) {
        const n = Math.round(v);
        const formatted = n >= 1000 ? n.toLocaleString() : String(n);
        el.textContent = formatted + suffix;
      },
    });
    return () => controls.stop();
  }, [isInView, to, suffix]);
  return <span ref={ref}>{fmt}</span>;
}

function GradientBorderCard({ children, className = "", style }: {
  children: React.ReactNode; className?: string; style?: React.CSSProperties;
}) {
  const [pos, setPos] = useState({ x: 0, y: 0, opacity: 0 });
  return (
    <div
      className={`relative rounded-[8px] h-full ${className}`}
      style={{
        padding: "1.5px",
        background: "linear-gradient(135deg, rgba(249,115,22,0.65) 0%, rgba(124,58,237,0.4) 50%, rgba(0,136,204,0.55) 100%)",
        ...style,
      }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({ x: e.clientX - r.left, y: e.clientY - r.top, opacity: 1 });
      }}
      onMouseLeave={() => setPos((p) => ({ ...p, opacity: 0 }))}
    >
      <div className="pointer-events-none absolute inset-0 rounded-[8px] transition-opacity duration-300 z-10"
        style={{ opacity: pos.opacity, background: `radial-gradient(500px circle at ${pos.x}px ${pos.y}px, rgba(249,115,22,0.12), transparent 60%)` }} />
      <div className="relative h-full rounded-[8px] overflow-hidden" style={{ background: "var(--bg-card)" }}>
        {children}
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

interface PublicSignal {
  id: string;
  asset: string;
  direction: string;
  confidence: number;
  outcome: string | null;
  outcome_price: number | null;
  created_at: string;
}
interface PublicSignalsMeta {
  total: number;
  hits: number;
  hit_rate: number | null;
  source: string;
}
interface PublicSignalsResponse {
  data: PublicSignal[];
  meta: PublicSignalsMeta;
}

interface MacroOutlook {
  regime: 'risk-on' | 'risk-off' | 'neutral';
  score: number;
  drivers: string[];
  upcomingEvents: Array<{ name: string; date: string; importance: string }>;
  breakdown: Record<string, number>;
}

interface SectorIntel {
  sectorName: string;
  score: number;
  verdict: string;
}

function verdictColor(verdict: string): string {
  if (verdict.includes('STRONG_BUY') || verdict === 'BUY') return '#22c55e';
  if (verdict === 'SELL' || verdict.includes('STRONG_SELL')) return '#ef4444';
  if (verdict === 'NEUTRAL') return '#60a5fa';
  return '#f97316';
}

function formatMacroDate(raw: string): string {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw.slice(0, 10) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function LandingPage() {
  const { address, isConnecting, connect } = useWallet();
  const { theme, toggle: toggleTheme } = useTheme();
  const [activeAgent, setActiveAgent] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [navScrolled, setNavScrolled] = useState(false);
  const [execStep, setExecStep] = useState(-1);
  const [heroTab, setHeroTab] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [liveTrackRecord, setLiveTrackRecord] = useState<PublicSignalsResponse | null>(null);
  const [trackRecordLoading, setTrackRecordLoading] = useState(true);
  const [macroOutlook, setMacroOutlook] = useState<MacroOutlook | null>(null);
  const [macroLoading, setMacroLoading] = useState(true);
  const [sectorIntel, setSectorIntel] = useState<SectorIntel[] | null>(null);
  const [sectorLoading, setSectorLoading] = useState(true);
  const [paperLeaderboard, setPaperLeaderboard] = useState<any[] | null>(null);
  const [marketLeaderboard, setMarketLeaderboard] = useState<any[] | null>(null);
  const execRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setTrackRecordLoading(true);
    fetch(`${API_URL}/api/public/signals`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => { if (json?.data && json?.meta) setLiveTrackRecord(json); })
      .catch(() => setLiveTrackRecord(null))
      .finally(() => setTrackRecordLoading(false));
  }, []);

  useEffect(() => {
    setMacroLoading(true);
    fetch(`${API_URL}/api/agents/macro`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => { if (json?.data) setMacroOutlook(json.data); })
      .catch(() => setMacroOutlook(null))
      .finally(() => setMacroLoading(false));
  }, []);

  useEffect(() => {
    setSectorLoading(true);
    fetch(`${API_URL}/api/sectors/intel`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const rows = Array.isArray(json?.data) ? json.data : [];
        setSectorIntel(rows.map((s: any) => ({
          sectorName: s.sectorName ?? s.sector ?? s.name ?? 'Sector',
          score: Number(s.score ?? 0),
          verdict: String(s.verdict ?? 'NEUTRAL'),
        })));
      })
      .catch(() => setSectorIntel(null))
      .finally(() => setSectorLoading(false));
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/paper/leaderboard?limit=3`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
        setPaperLeaderboard(rows.slice(0, 3));
      })
      .catch(() => setPaperLeaderboard([]));
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/marketplace/leaderboard?limit=3`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
        setMarketLeaderboard(rows.slice(0, 3));
      })
      .catch(() => setMarketLeaderboard([]));
  }, []);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    const handler = () => setNavScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setActiveAgent((a) => (a + 1) % AGENTS.length), 3000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = execRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
        let step = 0;
        const animate = () => { setExecStep(step); step++; if (step < EXECUTION_STEPS.length) setTimeout(animate, 500); };
        setTimeout(animate, 300);
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const winRateDisplay = liveTrackRecord?.meta?.hit_rate != null
    ? `${liveTrackRecord.meta.hit_rate}%`
    : trackRecordLoading
      ? '…'
      : '—';

  const signalCountDisplay = liveTrackRecord?.meta?.total != null
    ? String(liveTrackRecord.meta.total)
    : trackRecordLoading
      ? '…'
      : '—';

  const liveStats = STATS.map((s) =>
    s.label === 'Public Hit Rate' && liveTrackRecord?.meta?.hit_rate != null
      ? { ...s, to: liveTrackRecord.meta.hit_rate, fmt: `${liveTrackRecord.meta.hit_rate}%` }
      : s
  );

  const heroSignals = (liveTrackRecord?.data ?? []).slice(0, 4);

  const macroBreakdown = macroOutlook
    ? Object.entries(macroOutlook.breakdown).map(([key, value]) => ({
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        value,
      }))
    : [];

  const macroChartData = macroBreakdown.map((b) => ({ name: b.label.slice(0, 8), score: b.value }));

  return (
    <div data-theme={theme} className="min-h-screen overflow-x-hidden" style={{ background: "var(--bg-base)", color: "var(--text-primary)" }}>

      {/* Noise overlay */}
      <div className="pointer-events-none fixed inset-0 z-[1] opacity-[0.04]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundSize: "200px 200px",
      }} />

      {/* ── 1. NAVBAR ─────────────────────────────────────────────────────── */}
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] as [number,number,number,number] }}
        className="fixed top-0 left-0 right-0 z-[100] flex items-center justify-between px-6 md:px-12 h-16"
        style={{
          background: navScrolled ? (theme === "dark" ? "rgba(8,9,16,0.92)" : "rgba(255,255,255,0.92)") : "transparent",
          backdropFilter: navScrolled ? "blur(20px)" : "none",
          borderBottom: navScrolled ? "1px solid var(--glass-border)" : "none",
          transition: "background 0.3s",
        }}
      >
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark size={32} />
            <span
              className="font-black text-xl tracking-tight hidden sm:block"
              style={{
                fontFamily: 'var(--font-display)',
                letterSpacing: '-0.04em',
                color: theme === 'dark' ? '#f1f1f5' : '#111111',
              }}
            >
              SoSo<span style={{ color: '#f97316' }}>Mind</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((link) => link.href.startsWith('#') ? (
              <a key={link.label} href={link.href} className="text-sm font-medium transition-colors" style={{ color: "var(--text-secondary)" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-primary)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-secondary)"; }}>
                {link.label}
              </a>
            ) : (
              <Link key={link.label} to={link.href} className="text-sm font-medium transition-colors" style={{ color: "var(--text-secondary)" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-primary)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-secondary)"; }}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="https://t.me/SosoMindbot" target="_blank" rel="noopener noreferrer"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all"
            style={{ borderColor: "rgba(0,136,204,0.35)", color: "#0088cc", background: "rgba(0,136,204,0.08)" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "rgba(0,136,204,0.15)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "rgba(0,136,204,0.08)"; }}>
            <Send className="w-3.5 h-3.5" /> Telegram
          </a>
          <button onClick={toggleTheme} className="w-9 h-9 flex items-center justify-center rounded-full border transition-colors"
            style={{ borderColor: "var(--glass-border)", color: "var(--text-secondary)", background: "transparent" }}>
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {address ? (
            <Link to="/dashboard">
              <button className="px-4 py-2 rounded-[20px] text-sm font-bold text-white" style={{ background: "linear-gradient(135deg,#f97316 0%,#ea580c 50%,#c2410c 100%)" }}>
                Dashboard
              </button>
            </Link>
          ) : (
            <button onClick={connect} disabled={isConnecting}
              className="px-4 py-2 rounded-[20px] text-sm font-bold text-white disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#f97316 0%,#ea580c 50%,#c2410c 100%)" }}>
              {isConnecting ? "Connecting…" : "Launch Dashboard"}
            </button>
          )}
        </div>
      </motion.nav>

      {/* ── 2. HERO ───────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden px-6 pt-24 pb-0">

        {/* ── 3D Perspective Grid Background ────────────────────────────── */}
        <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden>
          {/* Base gradient */}
          <div className="absolute inset-0" style={{
            background: theme === "dark"
              ? "radial-gradient(ellipse at 30% 0%, rgba(249,115,22,0.22) 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, rgba(249,115,22,0.08) 0%, transparent 50%), #080910"
              : "radial-gradient(ellipse at 30% 0%, rgba(249,115,22,0.1) 0%, transparent 50%), #f8f8f5",
          }} />
          {/* 3D Perspective grid — SVG-based like SoDEX */}
          <svg
            className="absolute bottom-0 left-0 w-full"
            style={{ height: "65%", opacity: theme === "dark" ? 0.18 : 0.07 }}
            viewBox="0 0 1440 500" preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="gridFadeY" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f97316" stopOpacity="0" />
                <stop offset="70%" stopColor="#f97316" stopOpacity="1" />
                <stop offset="100%" stopColor="#f97316" stopOpacity="1" />
              </linearGradient>
              <linearGradient id="gridFadeX" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#f97316" stopOpacity="0" />
                <stop offset="20%" stopColor="#f97316" stopOpacity="1" />
                <stop offset="80%" stopColor="#f97316" stopOpacity="1" />
                <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Horizontal converging lines */}
            {[0.04,0.12,0.22,0.34,0.48,0.62,0.76,0.88,0.95,1].map((t, i) => (
              <line key={`h${i}`}
                x1={720 * (1 - t)} y1={t * 500}
                x2={720 + 720 * t} y2={t * 500}
                stroke="url(#gridFadeX)" strokeWidth="0.8" />
            ))}
            {/* Vertical converging lines radiating from horizon vanishing point */}
            {[-6,-4,-2.5,-1.5,-0.7,0,0.7,1.5,2.5,4,6].map((slope, i) => (
              <line key={`v${i}`}
                x1={720} y1={0}
                x2={720 + slope * 500} y2={500}
                stroke="url(#gridFadeY)" strokeWidth="0.8" />
            ))}
          </svg>
          {/* Animated glow orb */}
          <motion.div
            animate={{ opacity: [0.3, 0.55, 0.3], scale: [1, 1.12, 1] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute rounded-full blur-3xl pointer-events-none"
            style={{ width: 500, height: 320, top: "10%", left: "5%", background: "radial-gradient(ellipse, rgba(249,115,22,0.2) 0%, transparent 70%)" }}
          />
        </div>

        {/* ── Content: Left headline + Right panel ──────────────────────── */}
        <div className="relative z-10 w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-[calc(100vh-96px)] py-12">

          {/* ── LEFT: Headline + CTAs ──────────────────────────────────── */}
          <div className="flex flex-col justify-center">

            {/* Live badge */}
            <motion.div
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border self-start mb-8 text-xs font-semibold uppercase tracking-widest"
              style={{ borderColor: "rgba(249,115,22,0.35)", background: "rgba(249,115,22,0.08)", color: "#f97316" }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style={{ background: "#22c55e" }} />
              Live · SoDEX Mainnet
            </motion.div>

            {/* Big left-aligned headline */}
            <div className="mb-6">
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="text-xs font-bold uppercase tracking-[0.18em] mb-3"
                style={{ color: "var(--text-muted)" }}
              >
                Non-custodial agentic trading
              </motion.p>
              <SplitHeadline
                text={HERO_TAGLINE}
                className="font-bold leading-[1.08] tracking-[-0.025em]"
                style={{
                  fontSize: "clamp(2rem, 4.2vw, 3.8rem)",
                  fontFamily: "var(--font-display)",
                  color: "var(--text-primary)",
                } as React.CSSProperties}
              />
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.6, ease: [0.16, 1, 0.3, 1] as [number,number,number,number] }}
              className="text-[clamp(1rem,2vw,1.2rem)] mb-10 leading-relaxed max-w-lg"
              style={{ color: "var(--text-secondary)" }}
            >
              {HERO_SUB}
            </motion.p>

            {/* CTA buttons */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-3 mb-10"
            >
              <Link to="/track-record">
                <MagneticButton
                  className="flex items-center gap-2 px-7 py-3.5 rounded-[14px] font-bold text-white text-sm"
                  style={{ background: "linear-gradient(135deg,#f97316 0%,#ea580c 100%)" }}
                >
                  View Track Record <Target className="w-4 h-4" />
                </MagneticButton>
              </Link>
              <Link to="/trade">
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-2 px-7 py-3.5 rounded-[14px] font-medium text-sm border transition-colors"
                  style={{ borderColor: "var(--glass-border)", color: "var(--text-primary)", background: "transparent" }}
                >
                  Trade on SoDEX <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
              <a href="https://t.me/SosoMindbot" target="_blank" rel="noopener noreferrer">
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-2 px-7 py-3.5 rounded-[14px] font-medium text-sm border transition-colors"
                  style={{ borderColor: "rgba(0,136,204,0.35)", color: "#0088cc", background: "rgba(0,136,204,0.07)" }}
                >
                  <Send className="w-4 h-4" /> Telegram
                </motion.button>
              </a>
            </motion.div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="flex flex-wrap gap-x-6 gap-y-2"
            >
              {[
                ["35", "SoSoValue methods"],
                ["32", "SoDEX spot pairs"],
                [signalCountDisplay, "Resolved signals"],
                [winRateDisplay, "Hit rate"],
              ].map(([v, l]) => (
                <div key={l} className="flex items-baseline gap-1.5">
                  <span className="font-black text-base" style={{ color: "#f97316" }}>{v}</span>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>{l}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* ── RIGHT: Live Data Panel ─────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ delay: 0.35, duration: 0.85, ease: [0.16, 1, 0.3, 1] as [number,number,number,number] }}
            className="relative"
          >
            {/* Glow behind panel */}
            <div className="absolute -inset-4 rounded-[24px] blur-3xl pointer-events-none"
              style={{ background: "radial-gradient(ellipse, rgba(249,115,22,0.18) 0%, transparent 70%)" }} />

            <div
              className="relative rounded-[18px] border overflow-hidden"
              style={{
                borderColor: "rgba(249,115,22,0.2)",
                background: theme === "dark" ? "rgba(8,9,16,0.88)" : "rgba(255,255,255,0.88)",
                backdropFilter: "blur(28px)",
                boxShadow: "0 32px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(249,115,22,0.12)",
              }}
            >
              {/* Panel header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: "var(--glass-border)" }}>
                <div className="flex gap-1.5">
                  {["#ef4444","#eab308","#22c55e"].map((c) => (
                    <div key={c} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
                  ))}
                </div>
                <span className="text-xs font-semibold ml-2" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                  SoSoMind · Intelligence Hub
                </span>
                <span className="ml-auto flex items-center gap-1 text-[10px] font-bold" style={{ color: "#22c55e", fontFamily: "var(--font-mono)" }}>
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style={{ background: "#22c55e" }} />
                  LIVE
                </span>
              </div>

              {/* Tabs */}
              <div className="flex border-b" style={{ borderColor: "var(--glass-border)" }}>
                {["Signals","Agents","Macro"].map((tab, i) => (
                  <button
                    key={tab} onClick={() => setHeroTab(i)}
                    className="relative flex-1 py-2.5 text-xs font-semibold transition-colors"
                    style={{ color: heroTab === i ? "#f97316" : "var(--text-muted)", background: "transparent", border: "none" }}
                  >
                    {tab}
                    {heroTab === i && (
                      <motion.div
                        layoutId="heroTabLine"
                        className="absolute bottom-0 left-2 right-2 h-[2px] rounded-full"
                        style={{ background: "#f97316" }}
                        transition={{ type: "spring", stiffness: 400, damping: 35 }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="p-4" style={{ minHeight: 320 }}>
                <AnimatePresence mode="wait">

                  {/* Tab 0: Signals */}
                  {heroTab === 0 && (
                    <motion.div key="signals"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col gap-2"
                    >
                      {trackRecordLoading ? (
                        <ProductionState state="loading" compact />
                      ) : heroSignals.length === 0 ? (
                        <ProductionState state="unavailable" compact title="No public signals yet" message="Signals appear here once the research pipeline publishes outcomes." />
                      ) : heroSignals.map((sig, i) => {
                        const color = sig.direction === "LONG" ? "#22c55e" : sig.direction === "SHORT" ? "#ef4444" : "#f97316";
                        return (
                          <motion.div key={sig.id ?? i}
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.07, duration: 0.35 }}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-[10px]"
                            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
                          >
                            <div className="flex flex-col min-w-[72px]">
                              <span className="font-bold text-xs" style={{ color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>{sig.asset}</span>
                              <span className="text-[10px] font-bold uppercase" style={{ color }}>{sig.direction}</span>
                            </div>
                            <div className="flex flex-col items-end ml-auto">
                              <span className="text-xs font-black" style={{ color, fontFamily: "var(--font-mono)" }}>{sig.confidence}%</span>
                              <span className="text-[9px]" style={{ color: "var(--text-muted)" }}>confidence</span>
                            </div>
                            <div className="hidden sm:block w-16">
                              <div className="h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.07)" }}>
                                <motion.div
                                  initial={{ width: 0 }} animate={{ width: `${sig.confidence}%` }}
                                  transition={{ delay: 0.3 + i * 0.07, duration: 0.7 }}
                                  className="h-full rounded-full"
                                  style={{ background: color }}
                                />
                              </div>
                            </div>
                            {sig.outcome ? (
                              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{
                                background: sig.outcome === 'HIT' ? 'rgba(34,197,94,0.12)' : sig.outcome === 'STOP' ? 'rgba(239,68,68,0.12)' : 'rgba(96,165,250,0.12)',
                                color: sig.outcome === 'HIT' ? '#22c55e' : sig.outcome === 'STOP' ? '#ef4444' : '#60a5fa',
                              }}>{sig.outcome}</span>
                            ) : null}
                          </motion.div>
                        );
                      })}
                      <div className="mt-2 flex items-center gap-2 px-2">
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
                        <span className="text-[10px]" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Live from /api/public/signals</span>
                      </div>
                    </motion.div>
                  )}

                  {/* Tab 1: Agents */}
                  {heroTab === 1 && (
                    <motion.div key="agents"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                      className="flex flex-col gap-2"
                    >
                      {AGENTS.map((ag, i) => {
                        const Icon = ag.icon;
                        return (
                          <motion.div key={ag.name}
                            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.07, duration: 0.35 }}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-[10px]"
                            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
                          >
                            <div className="w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0"
                              style={{ background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.2)" }}>
                              <Icon className="w-4 h-4" style={{ color: "#f97316" }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>{ag.name}</div>
                              <div className="text-[10px] truncate" style={{ color: "var(--text-muted)" }}>{ag.desc.substring(0, 72)}…</div>
                            </div>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e" }}>LIVE</span>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  )}

                  {/* Tab 2: ETF Flow */}
                  {heroTab === 2 && (
                    <motion.div key="macro"
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                    >
                      {macroLoading ? (
                        <ProductionState state="loading" compact />
                      ) : !macroOutlook ? (
                        <ProductionState state="unavailable" compact title="Macro data unavailable" message="Macro regime loads from /api/agents/macro." />
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>Macro Regime Score</span>
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase" style={{
                              background: macroOutlook.regime === 'risk-on' ? "rgba(34,197,94,0.12)" : macroOutlook.regime === 'risk-off' ? "rgba(239,68,68,0.12)" : "rgba(249,115,22,0.12)",
                              color: macroOutlook.regime === 'risk-on' ? "#22c55e" : macroOutlook.regime === 'risk-off' ? "#ef4444" : "#f97316",
                            }}>{macroOutlook.regime}</span>
                          </div>
                          <div className="text-3xl font-black mb-4" style={{ color: "#f97316", fontFamily: "var(--font-mono)" }}>{macroOutlook.score}/100</div>
                          <div style={{ height: 160 }}>
                            {mounted && macroChartData.length > 0 && (
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={macroChartData}>
                                  <defs>
                                    <linearGradient id="macroBreakdown" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor="#f97316" stopOpacity={0.4} />
                                      <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} />
                                  <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} />
                                  <Area type="monotone" dataKey="score" stroke="#f97316" fill="url(#macroBreakdown)" strokeWidth={2} dot={false} isAnimationActive />
                                </AreaChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                          <div className="mt-3 flex flex-col gap-1">
                            {(macroOutlook.drivers.slice(0, 3)).map((d) => (
                              <span key={d} className="text-[10px]" style={{ color: "var(--text-muted)" }}>• {d}</span>
                            ))}
                          </div>
                        </>
                      )}
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>

              {/* Panel footer */}
              <div className="px-4 py-2.5 border-t flex items-center gap-2" style={{ borderColor: "var(--glass-border)" }}>
                <span className="text-[10px]" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Powered by 13 live data sources · MCP protocol</span>
                <div className="ml-auto flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#f97316" }} />
                  <span className="text-[10px] font-semibold" style={{ color: "#f97316", fontFamily: "var(--font-mono)" }}>STREAMING</span>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
        >
          <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Scroll</span>
          <motion.div
            animate={{ y: [0, 6, 0] }} transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          </motion.div>
        </motion.div>

      </section>

      {/* ── 3. PARTNERS MARQUEE + STATS ───────────────────────────────────── */}
      <section className="border-y overflow-hidden py-4" style={{ borderColor: "var(--glass-border)" }}>
        <div className="flex gap-10 whitespace-nowrap" style={{ animation: "marquee 28s linear infinite" }}>
          {[...PARTNERS, ...PARTNERS].map((p, i) => (
            <span key={i} className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
              <span className="w-1 h-1 rounded-full inline-block" style={{ background: "rgba(249,115,22,0.5)" }} />
              {p}
            </span>
          ))}
        </div>
      </section>

      <section className="py-20 border-b" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center text-xs font-bold uppercase tracking-widest mb-10" style={{ color: "var(--text-muted)" }}
          >Platform at a glance</motion.p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
            {liveStats.map((s, i) => (
              <motion.div key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
              >
                <div className="text-4xl font-black mb-1" style={{ fontFamily: "var(--font-display)", color: "#f97316" }}>
                  <AnimatedStat to={s.to} suffix={s.suffix} fmt={s.fmt} />
                </div>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. ARCHITECTURE DIAGRAM ───────────────────────────────────────── */}
      <section id="architecture" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-center" style={{ color: "#f97316" }}>Architecture</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-black text-center mb-4 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Agents working{" "}
              <span style={{
                background: "linear-gradient(135deg, #f97316, #ea580c)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>in concert</span>
            </h2>
            <p className="text-center max-w-xl mx-auto mb-16" style={{ color: "var(--text-secondary)" }}>Specialist AI agents collaborate in real time — each with its own domain expertise, all coordinated by the Orchestrator.</p>
          </motion.div>
          <div className="flex flex-col md:flex-row items-stretch justify-between gap-4 md:gap-0 mb-12">

            {/* Node 1: Data Sources */}
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.55 }}
              className="flex-1"
            >
              <SpotlightCard className="p-6 h-full flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-[10px] flex items-center justify-center mb-4"
                  style={{ background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.3)" }}>
                  <Database className="w-6 h-6" style={{ color: "#8b5cf6" }} />
                </div>
                <div className="font-bold text-base mb-1" style={{ color: "var(--text-primary)" }}>13 Data Sources</div>
                <div className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>MCP · REST · WebSocket</div>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {["SoSoValue", "SoDEX", "Binance", "Macro", "Social"].map((s) => (
                    <span key={s} className="px-2 py-0.5 rounded text-[10px] font-semibold"
                      style={{ background: "rgba(139,92,246,0.1)", color: "#8b5cf6" }}>{s}</span>
                  ))}
                </div>
              </SpotlightCard>
            </motion.div>

            {/* Connector 1 */}
            <div className="flex flex-col items-center justify-center md:w-14 flex-shrink-0 py-4 md:py-0">
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="hidden md:block h-0.5 w-full origin-left"
                style={{ background: "linear-gradient(90deg, rgba(139,92,246,0.8), #f97316)" }} />
              <motion.div initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="md:hidden w-0.5 h-8"
                style={{ background: "linear-gradient(to bottom, rgba(139,92,246,0.8), #f97316)" }} />
            </div>

            {/* Node 2: Orchestrator (center) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }} transition={{ delay: 0.18, duration: 0.6, ease: [0.16,1,0.3,1] as [number,number,number,number] }}
              className="flex-1 relative"
            >
              <motion.div
                animate={{ scale: [1, 1.04, 1], opacity: [0.4, 0.1, 0.4] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-[-4px] rounded-[10px] pointer-events-none"
                style={{ border: "1px solid rgba(249,115,22,0.35)" }}
              />
              <GradientBorderCard className="h-full">
                <div className="p-6 flex flex-col items-center text-center h-full">
                  <div className="w-12 h-12 rounded-[10px] flex items-center justify-center mb-4"
                    style={{ background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.4)" }}>
                    <Network className="w-6 h-6" style={{ color: "#f97316" }} />
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-black text-base" style={{ color: "#f97316" }}>Orchestrator</span>
                    <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
                  </div>
                  <div className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Central Coordination Hub</div>
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {AGENTS.filter((a) => a.name !== "Orchestrator").map((a) => (
                      <span key={a.name} className="px-2 py-0.5 rounded text-[10px] font-semibold"
                        style={{ background: "rgba(249,115,22,0.1)", color: "#f97316" }}>{a.name}</span>
                    ))}
                  </div>
                </div>
              </GradientBorderCard>
            </motion.div>

            {/* Connector 2 */}
            <div className="flex flex-col items-center justify-center md:w-14 flex-shrink-0 py-4 md:py-0">
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="hidden md:block h-0.5 w-full origin-left"
                style={{ background: "linear-gradient(90deg, #f97316, rgba(34,197,94,0.8))" }} />
              <motion.div initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="md:hidden w-0.5 h-8"
                style={{ background: "linear-gradient(to bottom, #f97316, rgba(34,197,94,0.8))" }} />
            </div>

            {/* Node 3: Signals & Execution */}
            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }} transition={{ delay: 0.36, duration: 0.55 }}
              className="flex-1"
            >
              <SpotlightCard className="p-6 h-full flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-[10px] flex items-center justify-center mb-4"
                  style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)" }}>
                  <Zap className="w-6 h-6" style={{ color: "#22c55e" }} />
                </div>
                <div className="font-bold text-base mb-1" style={{ color: "var(--text-primary)" }}>Signals & Actions</div>
                <div className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Real-time output</div>
                <div className="flex flex-wrap gap-1.5 justify-center">
                  {["DEX Orders", "Push Alerts", "Reports", "Telegram"].map((s) => (
                    <span key={s} className="px-2 py-0.5 rounded text-[10px] font-semibold"
                      style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>{s}</span>
                  ))}
                </div>
              </SpotlightCard>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ── 5. FEATURES BENTO GRID ────────────────────────────────────────── */}
      <section id="features" className="py-20 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6" style={{ background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.25)", color: "#f97316" }}>
              Features
            </span>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-black text-center mb-4 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              {FEATURES.length} production modules.{" "}
              <span style={{ background: "linear-gradient(135deg, #f97316, #ea580c)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>One platform.</span>
            </h2>
            <p className="text-center max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>Live SoSoValue intelligence, SSI scoring, risk preflight, and non-custodial SoDEX mainnet execution — no mock data.</p>
          </motion.div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

            {/* ── LARGE: NLP Intent Trading ── */}
            <motion.div className="md:col-span-2 md:row-span-2" style={{ minHeight: 360 }}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55 }}>
              <GradientBorderCard className="h-full">
                <div className="p-6 flex flex-col h-full relative overflow-hidden" style={{ minHeight: 360 }}>
                  {/* SVG: NLP parse flow diagram */}
                  <svg className="absolute right-0 bottom-0 opacity-[0.12] pointer-events-none" width="220" height="180" viewBox="0 0 220 180" fill="none">
                    <rect x="10" y="60" width="56" height="28" rx="6" stroke="#f97316" strokeWidth="1.5" />
                    <text x="38" y="78" textAnchor="middle" fill="#f97316" fontSize="9" fontFamily="monospace">INPUT</text>
                    <line x1="66" y1="74" x2="90" y2="74" stroke="#f97316" strokeWidth="1.5" strokeDasharray="4 2" />
                    <rect x="90" y="56" width="56" height="36" rx="6" stroke="#f97316" strokeWidth="1.5" />
                    <text x="118" y="72" textAnchor="middle" fill="#f97316" fontSize="8" fontFamily="monospace">NLP</text>
                    <text x="118" y="84" textAnchor="middle" fill="#f97316" fontSize="8" fontFamily="monospace">AGENT</text>
                    <line x1="146" y1="74" x2="170" y2="74" stroke="#f97316" strokeWidth="1.5" strokeDasharray="4 2" />
                    <rect x="170" y="50" width="42" height="20" rx="4" stroke="#22c55e" strokeWidth="1.2" />
                    <text x="191" y="63" textAnchor="middle" fill="#22c55e" fontSize="7" fontFamily="monospace">BUY</text>
                    <rect x="170" y="76" width="42" height="20" rx="4" stroke="#eab308" strokeWidth="1.2" />
                    <text x="191" y="89" textAnchor="middle" fill="#eab308" fontSize="7" fontFamily="monospace">HEDGE</text>
                    <rect x="170" y="102" width="42" height="20" rx="4" stroke="#ef4444" strokeWidth="1.2" />
                    <text x="191" y="115" textAnchor="middle" fill="#ef4444" fontSize="7" fontFamily="monospace">EXIT</text>
                    <line x1="191" y1="70" x2="191" y2="76" stroke="#f97316" strokeWidth="1" strokeDasharray="2 2" />
                    <line x1="191" y1="96" x2="191" y2="102" stroke="#f97316" strokeWidth="1" strokeDasharray="2 2" />
                    <circle cx="38" cy="110" r="16" stroke="#f97316" strokeWidth="1.2" strokeDasharray="3 2" />
                    <text x="38" y="114" textAnchor="middle" fill="#f97316" fontSize="8" fontFamily="monospace">VOICE</text>
                    <line x1="50" y1="100" x2="90" y2="86" stroke="#f97316" strokeWidth="1" strokeDasharray="3 2" />
                  </svg>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 relative z-10"
                    style={{ background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.35)" }}>
                    <MessageSquare className="w-5 h-5" style={{ color: "#f97316" }} />
                  </div>
                  <div className="font-bold text-lg mb-2 relative z-10" style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>NLP Intent Trading</div>
                  <p className="text-sm leading-relaxed relative z-10 mb-6" style={{ color: "var(--text-secondary)" }}>Type in plain English, execute with precision. Our NLP agent parses your intent and routes it to the right strategy.</p>
                  {/* Animated chat bubbles */}
                  <div className="flex flex-col gap-2 relative z-10 mt-auto">
                    {[
                      { msg: "Buy 0.1 ETH if RSI < 35", delay: 0 },
                      { msg: "↳ Routing to SoDEX execution…", delay: 0.2, muted: true },
                      { msg: "✓ Order filled at $2,381", delay: 0.4, green: true },
                    ].map((b, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: b.delay + 0.3, duration: 0.4 }}
                        className="text-xs px-3 py-1.5 rounded-lg self-start"
                        style={{
                          background: b.green ? "rgba(34,197,94,0.12)" : "rgba(249,115,22,0.08)",
                          border: `1px solid ${b.green ? "rgba(34,197,94,0.25)" : "rgba(249,115,22,0.18)"}`,
                          color: b.green ? "#22c55e" : b.muted ? "var(--text-muted)" : "var(--text-primary)",
                          fontFamily: "var(--font-mono)",
                        }}>
                        {b.msg}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </GradientBorderCard>
            </motion.div>

            {/* ── LARGE: Signal Marketplace ── */}
            <motion.div className="md:col-span-2 md:row-span-2" style={{ minHeight: 360 }}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: 0.08, duration: 0.55 }}>
              <GradientBorderCard className="h-full">
                <div className="p-6 flex flex-col h-full relative overflow-hidden" style={{ minHeight: 360 }}>
                  {/* SVG: signal wave diagram */}
                  <svg className="absolute right-0 top-0 opacity-[0.13] pointer-events-none" width="180" height="100" viewBox="0 0 180 100" fill="none">
                    <polyline points="0,70 20,50 40,65 60,30 80,55 100,20 120,45 140,25 160,40 180,15" stroke="#f97316" strokeWidth="2" fill="none" strokeLinecap="round" />
                    <circle cx="100" cy="20" r="5" fill="#f97316" fillOpacity="0.7" />
                    <circle cx="60" cy="30" r="4" fill="#f97316" fillOpacity="0.5" />
                    <circle cx="140" cy="25" r="4" fill="#22c55e" fillOpacity="0.6" />
                  </svg>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.35)" }}>
                    <Zap className="w-5 h-5" style={{ color: "#f97316" }} />
                  </div>
                  <div className="font-bold text-lg mb-2" style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>Signal Marketplace</div>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--text-secondary)" }}>Subscribe to curated signal streams from top-performing strategies, each verified with live backtest performance.</p>
                  {/* Signal rows */}
                  <div className="flex flex-col gap-2 mt-auto">
                    {[
                      { pair: "BTC/USDT", dir: "LONG", win: "74%", color: "#22c55e" },
                      { pair: "ETH/USDT", dir: "LONG", win: "69%", color: "#22c55e" },
                      { pair: "SOL/USDT", dir: "SHORT", win: "61%", color: "#ef4444" },
                    ].map((s, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: 10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 + 0.2 }}
                        className="flex items-center justify-between px-3 py-2 rounded-lg"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--glass-border)" }}>
                        <span className="text-xs font-mono font-bold" style={{ color: "var(--text-primary)" }}>{s.pair}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${s.color}18`, color: s.color }}>{s.dir}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 rounded-full" style={{ background: "var(--surface-2)" }}>
                            <motion.div className="h-1.5 rounded-full" style={{ background: s.color }} initial={{ width: 0 }} whileInView={{ width: s.win }} viewport={{ once: true }} transition={{ delay: i * 0.12 + 0.4, duration: 0.7 }} />
                          </div>
                          <span className="text-[10px] font-mono" style={{ color: s.color }}>{s.win}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </GradientBorderCard>
            </motion.div>

            {/* ── MEDIUM: Arbitrage Scanner ── */}
            <motion.div className="md:col-span-2" style={{ minHeight: 180 }}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: 0.1, duration: 0.5 }}>
              <SpotlightCard className="h-full">
                <div className="p-5 flex gap-4 h-full items-start" style={{ minHeight: 180 }}>
                  {/* SVG: exchange spread diagram */}
                  <svg width="90" height="72" viewBox="0 0 90 72" fill="none" className="flex-shrink-0 mt-1 opacity-80">
                    <rect x="1" y="18" width="26" height="36" rx="5" stroke="#f97316" strokeWidth="1.3" fill="rgba(249,115,22,0.07)" />
                    <text x="14" y="34" textAnchor="middle" fill="#f97316" fontSize="7" fontFamily="monospace">CEX</text>
                    <text x="14" y="44" textAnchor="middle" fill="#f97316" fontSize="7" fontFamily="monospace">$63.1k</text>
                    <rect x="63" y="18" width="26" height="36" rx="5" stroke="#22c55e" strokeWidth="1.3" fill="rgba(34,197,94,0.07)" />
                    <text x="76" y="34" textAnchor="middle" fill="#22c55e" fontSize="7" fontFamily="monospace">DEX</text>
                    <text x="76" y="44" textAnchor="middle" fill="#22c55e" fontSize="7" fontFamily="monospace">$63.4k</text>
                    <line x1="27" y1="36" x2="63" y2="36" stroke="#eab308" strokeWidth="1.5" strokeDasharray="4 2" />
                    <polygon points="59,32 63,36 59,40" fill="#eab308" />
                    <text x="45" y="28" textAnchor="middle" fill="#eab308" fontSize="7" fontFamily="monospace">+$300</text>
                    <text x="45" y="52" textAnchor="middle" fill="#eab308" fontSize="7" fontFamily="monospace">SPREAD</text>
                    <circle cx="45" cy="36" r="4" fill="#eab308" fillOpacity="0.25" />
                    <circle cx="45" cy="36" r="2" fill="#eab308" />
                  </svg>
                  <div className="flex flex-col justify-between flex-1 h-full">
                    <div>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.25)" }}>
                        <Search className="w-4 h-4" style={{ color: "#f97316" }} />
                      </div>
                      <div className="font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>Arbitrage Scanner</div>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>Real-time cross-exchange spread detection with slippage-adjusted execution paths.</p>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
                      <span className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>3 spreads live · avg +0.47%</span>
                    </div>
                  </div>
                </div>
              </SpotlightCard>
            </motion.div>

            {/* ── MEDIUM: Whale Tracker ── */}
            <motion.div className="md:col-span-2" style={{ minHeight: 180 }}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: 0.15, duration: 0.5 }}>
              <SpotlightCard className="h-full">
                <div className="p-5 flex gap-4 h-full items-start" style={{ minHeight: 180 }}>
                  {/* SVG: whale flow */}
                  <svg width="90" height="72" viewBox="0 0 90 72" fill="none" className="flex-shrink-0 mt-1 opacity-80">
                    <circle cx="20" cy="20" r="12" stroke="#f97316" strokeWidth="1.3" fill="rgba(249,115,22,0.08)" />
                    <text x="20" y="24" textAnchor="middle" fill="#f97316" fontSize="8" fontFamily="monospace">🐋</text>
                    <circle cx="70" cy="20" r="8" stroke="#3b82f6" strokeWidth="1.3" fill="rgba(59,130,246,0.08)" />
                    <text x="70" y="24" textAnchor="middle" fill="#3b82f6" fontSize="7" fontFamily="monospace">CEX</text>
                    <line x1="32" y1="20" x2="62" y2="20" stroke="#f97316" strokeWidth="1.5" strokeDasharray="4 2" />
                    <polygon points="58,16 62,20 58,24" fill="#f97316" />
                    <text x="45" y="14" textAnchor="middle" fill="#eab308" fontSize="7" fontFamily="monospace">$14.2M</text>
                    <circle cx="20" cy="54" r="8" stroke="#f97316" strokeWidth="1.3" fill="rgba(249,115,22,0.08)" />
                    <text x="20" y="58" textAnchor="middle" fill="#f97316" fontSize="7" fontFamily="monospace">COLD</text>
                    <circle cx="70" cy="54" r="8" stroke="#22c55e" strokeWidth="1.3" fill="rgba(34,197,94,0.08)" />
                    <text x="70" y="58" textAnchor="middle" fill="#22c55e" fontSize="7" fontFamily="monospace">DEX</text>
                    <line x1="28" y1="54" x2="62" y2="54" stroke="#22c55e" strokeWidth="1.5" strokeDasharray="4 2" />
                    <polygon points="58,50 62,54 58,58" fill="#22c55e" />
                  </svg>
                  <div className="flex flex-col justify-between flex-1 h-full">
                    <div>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.25)" }}>
                        <Eye className="w-4 h-4" style={{ color: "#f97316" }} />
                      </div>
                      <div className="font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>Whale Tracker</div>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>Follow on-chain wallet movements of known large traders with entry/exit alerts.</p>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#f97316" }} />
                      <span className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>$14.2M moved · 2m ago</span>
                    </div>
                  </div>
                </div>
              </SpotlightCard>
            </motion.div>

            {/* ── MEDIUM: Voice Trading ── */}
            <motion.div className="md:col-span-2" style={{ minHeight: 180 }}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: 0.2, duration: 0.5 }}>
              <SpotlightCard className="h-full">
                <div className="p-5 flex gap-4 h-full items-start" style={{ minHeight: 180 }}>
                  {/* SVG: waveform */}
                  <svg width="80" height="72" viewBox="0 0 80 72" fill="none" className="flex-shrink-0 mt-1 opacity-85">
                    {[8,16,24,32,40,48,56,64,72].map((x, i) => {
                      const h = [10,22,36,48,56,44,30,18,10][i];
                      return <rect key={x} x={x - 3} y={(72-h)/2} width="5" height={h} rx="2.5" fill="#f97316" fillOpacity={0.35 + i*0.07} />;
                    })}
                    <circle cx="40" cy="36" r="14" stroke="#f97316" strokeWidth="1" strokeDasharray="3 2" />
                  </svg>
                  <div className="flex flex-col justify-between flex-1 h-full">
                    <div>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.25)" }}>
                        <Mic className="w-4 h-4" style={{ color: "#f97316" }} />
                      </div>
                      <div className="font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>Voice Trading</div>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>Hands-free trading via voice commands. Powered by Whisper AI and integrated directly into SoDEX execution.</p>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#a855f7" }} />
                      <span className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>Whisper AI · &lt;300ms latency</span>
                    </div>
                  </div>
                </div>
              </SpotlightCard>
            </motion.div>

            {/* ── SMALL CARDS (remaining 9) ── */}
            {FEATURES.slice(5).map((f, i) => {
              const Icon = f.icon;
              // Micro SVG diagrams per card
              const microSvgs: Record<string, React.ReactNode> = {
                "Portfolio Rebalancer": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="22" r="18" stroke="#f97316" strokeWidth="1.2" fill="none" />
                    <path d="M22 22 L22 6 A16 16 0 0 1 36 28 Z" fill="rgba(249,115,22,0.25)" />
                    <path d="M22 22 L36 28 A16 16 0 0 1 8 28 Z" fill="rgba(59,130,246,0.2)" />
                    <path d="M22 22 L8 28 A16 16 0 0 1 22 6 Z" fill="rgba(34,197,94,0.18)" />
                    <circle cx="22" cy="22" r="4" fill="#f97316" fillOpacity="0.7" />
                  </svg>
                ),
                "Paper Trading": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <polyline points="4,36 12,24 20,28 28,14 36,18 44,6" stroke="#f97316" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    <polyline points="4,36 12,28 20,32 28,20 36,24 44,12" stroke="#3b82f6" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeDasharray="3 2" />
                    <circle cx="28" cy="14" r="3" fill="#f97316" fillOpacity="0.7" />
                  </svg>
                ),
                "Confluence Engine": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <circle cx="14" cy="22" r="10" stroke="#f97316" strokeWidth="1.2" fill="rgba(249,115,22,0.08)" />
                    <circle cx="30" cy="22" r="10" stroke="#3b82f6" strokeWidth="1.2" fill="rgba(59,130,246,0.08)" />
                    <ellipse cx="22" cy="22" rx="4" ry="8" fill="rgba(249,115,22,0.22)" />
                    <circle cx="22" cy="22" r="2" fill="#f97316" />
                  </svg>
                ),
                "Kelly Criterion": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <rect x="4" y="28" width="8" height="12" rx="2" fill="rgba(249,115,22,0.5)" />
                    <rect x="16" y="18" width="8" height="22" rx="2" fill="rgba(249,115,22,0.7)" />
                    <rect x="28" y="10" width="8" height="30" rx="2" fill="#f97316" />
                    <polyline points="4,28 12,20 20,18 28,10 36,10" stroke="#22c55e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    <circle cx="28" cy="10" r="3" fill="#22c55e" />
                  </svg>
                ),
                "Social Sentiment": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="22" r="14" stroke="#f97316" strokeWidth="1.2" fill="none" />
                    <path d="M15 20 Q22 14 29 20" stroke="#22c55e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    <circle cx="17" cy="23" r="1.5" fill="#f97316" />
                    <circle cx="27" cy="23" r="1.5" fill="#f97316" />
                    <path d="M16 28 Q22 34 28 28" stroke="#f97316" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    {/* Pre-computed: r=20, center=(22,22), angles 0/60/120/180/240/300° */}
                    <circle cx="42" cy="22" r="2" fill="#f97316" fillOpacity="0.4" />
                    <circle cx="32" cy="39.32" r="2" fill="#f97316" fillOpacity="0.4" />
                    <circle cx="12" cy="39.32" r="2" fill="#f97316" fillOpacity="0.4" />
                    <circle cx="2" cy="22" r="2" fill="#f97316" fillOpacity="0.4" />
                    <circle cx="12" cy="4.68" r="2" fill="#f97316" fillOpacity="0.4" />
                    <circle cx="32" cy="4.68" r="2" fill="#f97316" fillOpacity="0.4" />
                  </svg>
                ),
                "Tax Reporting": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <rect x="8" y="4" width="28" height="36" rx="4" stroke="#f97316" strokeWidth="1.2" fill="rgba(249,115,22,0.06)" />
                    <line x1="13" y1="14" x2="31" y2="14" stroke="#f97316" strokeWidth="1" strokeOpacity="0.6" />
                    <line x1="13" y1="20" x2="31" y2="20" stroke="#f97316" strokeWidth="1" strokeOpacity="0.4" />
                    <line x1="13" y1="26" x2="25" y2="26" stroke="#f97316" strokeWidth="1" strokeOpacity="0.3" />
                    <circle cx="31" cy="32" r="6" fill="rgba(34,197,94,0.15)" stroke="#22c55e" strokeWidth="1.2" />
                    <polyline points="27,32 30,35 35,29" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                  </svg>
                ),
                "MEV Protection": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <path d="M22 4 L38 12 L38 24 Q38 36 22 42 Q6 36 6 24 L6 12 Z" stroke="#f97316" strokeWidth="1.3" fill="rgba(249,115,22,0.07)" />
                    <polyline points="15,22 20,27 30,17" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" fill="none" />
                    <circle cx="22" cy="22" r="3" fill="#f97316" fillOpacity="0.3" />
                  </svg>
                ),
                "Trader Persona": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <circle cx="22" cy="16" r="8" stroke="#f97316" strokeWidth="1.3" fill="rgba(249,115,22,0.1)" />
                    <path d="M8 38 Q8 28 22 28 Q36 28 36 38" stroke="#f97316" strokeWidth="1.3" fill="none" strokeLinecap="round" />
                    {/* Pre-computed spider lines: r=20, vals=[0.8,0.5,0.9,0.6,0.7,0.4,0.85,0.65], center=(22,22) */}
                    <line x1="22" y1="22" x2="38" y2="22" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.4" />
                    <line x1="22" y1="22" x2="29.07" y2="29.07" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.4" />
                    <line x1="22" y1="22" x2="22" y2="40" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.4" />
                    <line x1="22" y1="22" x2="13.51" y2="30.49" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.4" />
                    <line x1="22" y1="22" x2="8" y2="22" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.4" />
                    <line x1="22" y1="22" x2="16.34" y2="16.34" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.4" />
                    <line x1="22" y1="22" x2="22" y2="5" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.4" />
                    <line x1="22" y1="22" x2="31.19" y2="12.81" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.4" />
                  </svg>
                ),
                "Funding Signals": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <line x1="4" y1="22" x2="40" y2="22" stroke="#f97316" strokeWidth="1" strokeOpacity="0.3" />
                    <polyline points="4,30 10,25 16,28 22,16 28,20 34,12 40,16" stroke="#22c55e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    <polyline points="4,18 10,22 16,19 22,28 28,24 34,30 40,26" stroke="#ef4444" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeDasharray="3 2" />
                    <text x="22" y="40" textAnchor="middle" fill="#f97316" fontSize="7" fontFamily="monospace">FUNDING RATE</text>
                  </svg>
                ),
                "Macro Playbook": (
                  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                    <rect x="6" y="6" width="32" height="32" rx="4" stroke="#f97316" strokeWidth="1.2" fill="rgba(249,115,22,0.05)" />
                    <rect x="11" y="11" width="10" height="10" rx="2" fill="rgba(249,115,22,0.3)" />
                    <rect x="23" y="11" width="10" height="10" rx="2" fill="rgba(59,130,246,0.25)" />
                    <rect x="11" y="23" width="10" height="10" rx="2" fill="rgba(34,197,94,0.25)" />
                    <rect x="23" y="23" width="10" height="10" rx="2" fill="rgba(234,179,8,0.25)" />
                    <line x1="22" y1="6" x2="22" y2="38" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.3" />
                    <line x1="6" y1="22" x2="38" y2="22" stroke="#f97316" strokeWidth="0.8" strokeOpacity="0.3" />
                  </svg>
                ),
              };

              return (
                <motion.div key={f.title} className="md:col-span-1"
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: (i % 4) * 0.07, duration: 0.45 }}>
                  <SpotlightCard className="h-full">
                    <div className="p-4 flex flex-col gap-3" style={{ minHeight: 160 }}>
                      <div className="flex items-start justify-between">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: "rgba(249,115,22,0.11)", border: "1px solid rgba(249,115,22,0.22)" }}>
                          <Icon className="w-4 h-4" style={{ color: "#f97316" }} />
                        </div>
                        <div className="opacity-60 flex-shrink-0">
                          {microSvgs[f.title]}
                        </div>
                      </div>
                      <div className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{f.title}</div>
                      <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{f.desc}</p>
                    </div>
                  </SpotlightCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 6. AI AGENTS DEEP DIVE ────────────────────────────────────────── */}
      <section className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "#f97316" }}>AI Agents</p>
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] font-black mb-8 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>Specialist agents,<br />working for you</h2>
            <div className="flex flex-col gap-3">
              {AGENTS.map((agent, i) => {
                const Icon = agent.icon;
                const isActive = activeAgent === i;
                return (
                  <motion.div key={agent.name}
                    onClick={() => setActiveAgent(i)}
                    animate={{ opacity: isActive ? 1 : 0.45, scale: isActive ? 1 : 0.98 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center gap-4 p-4 rounded-[8px] cursor-pointer border transition-all"
                    style={{
                      borderColor: isActive ? "rgba(249,115,22,0.4)" : "var(--glass-border)",
                      background: isActive ? "rgba(249,115,22,0.06)" : "var(--bg-card)",
                    }}
                  >
                    <div className="w-9 h-9 rounded-[8px] flex items-center justify-center flex-shrink-0"
                      style={{ background: isActive ? "rgba(249,115,22,0.15)" : "var(--surface-2)" }}>
                      <Icon className="w-4 h-4" style={{ color: isActive ? "#f97316" : "var(--text-muted)" } as React.CSSProperties} />
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{agent.name}</span>
                      {isActive && <span className="w-2 h-2 rounded-full animate-pulse inline-block" style={{ background: "#22c55e" }} />}
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e", fontFamily: "var(--font-mono)" }}>AGENT</span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <AnimatePresence mode="wait">
              <motion.div key={activeAgent}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                className="p-6 rounded-[8px] border"
                style={{ borderColor: "rgba(249,115,22,0.3)", background: "var(--bg-card)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
                  <span className="font-bold" style={{ color: "#f97316" }}>{AGENTS[activeAgent].name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(249,115,22,0.1)", color: "#f97316", fontFamily: "var(--font-mono)" }}>ACTIVE</span>
                </div>
                <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--text-secondary)" }}>{AGENTS[activeAgent].desc}</p>
                <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--glass-border)" }}>
                  <div className="text-xs font-bold mb-3 uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Circuit Breaker Levels</div>
                  {[
                    { label: "Warning", pct: 60, color: "#eab308" },
                    { label: "Reduce",  pct: 75, color: "#f97316" },
                    { label: "Halt",    pct: 90, color: "#ef4444" },
                  ].map((level) => (
                    <div key={level.label} className="flex items-center gap-3 mb-2">
                      <span className="text-xs w-16" style={{ color: "var(--text-muted)" }}>{level.label}</span>
                      <div className="flex-1 h-1.5 rounded-full relative" style={{ background: "var(--surface-2)" }}>
                        <div className="absolute top-0 bottom-0 left-0 rounded-full" style={{ width: `${level.pct}%`, background: level.color, opacity: 0.7 }} />
                      </div>
                      <span className="text-xs" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{level.pct}%</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* ── 7. SIGNAL ENGINE ──────────────────────────────────────────────── */}
      <section className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-center" style={{ color: "#f97316" }}>Signal Engine</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-black text-center mb-4 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Real-time{" "}
              <span style={{
                background: "linear-gradient(135deg, #f97316, #ea580c)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>trading signals</span>
            </h2>
            <p className="text-center max-w-xl mx-auto mb-12" style={{ color: "var(--text-secondary)" }}>
              Evidence-first directional signals from the live research pipeline — tracked on the public outcome ledger.
            </p>
            <div className="flex items-center justify-center gap-2 mb-8">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
              <span className="text-xs font-semibold" style={{ color: "#22c55e", fontFamily: "var(--font-mono)" }}>LIVE FROM API</span>
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>· /api/public/signals</span>
            </div>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {trackRecordLoading ? (
              <ProductionState state="loading" className="md:col-span-4" />
            ) : heroSignals.length === 0 ? (
              <ProductionState state="unavailable" className="md:col-span-4" title="No public signals yet" message="Published signals with confidence and outcomes appear here." />
            ) : heroSignals.map((sig, i) => {
              const color = sig.direction === "LONG" ? "#22c55e" : sig.direction === "SHORT" ? "#ef4444" : "#60a5fa";
              return (
                <motion.div key={sig.id ?? i}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                >
                  <SpotlightCard className="p-4 flex flex-col gap-3" style={{
                    borderColor: sig.direction === "LONG" ? "rgba(34,197,94,0.2)" : sig.direction === "SHORT" ? "rgba(239,68,68,0.2)" : undefined,
                    background: sig.direction === "LONG" ? "rgba(34,197,94,0.03)" : sig.direction === "SHORT" ? "rgba(239,68,68,0.03)" : undefined,
                  }}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{sig.asset}</span>
                      <SignalBadge dir={sig.direction} />
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1" style={{ color: "var(--text-muted)" }}>
                        <span>Confidence</span>
                        <span style={{ fontFamily: "var(--font-mono)" }}>{sig.confidence}%</span>
                      </div>
                      <div className="h-1.5 rounded-full" style={{ background: "var(--surface-2)" }}>
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${sig.confidence}%` }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.3 + i * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] as [number,number,number,number] }}
                          className="h-1.5 rounded-full"
                          style={{ background: color }}
                        />
                      </div>
                    </div>
                    <span className="text-[10px] font-bold" style={{
                      color: sig.outcome === 'HIT' ? '#22c55e' : sig.outcome === 'STOP' ? '#ef4444' : 'var(--text-muted)',
                      fontFamily: "var(--font-mono)",
                    }}>{sig.outcome ?? 'ACTIVE'}</span>
                  </SpotlightCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 8. SSI SECTOR INTELLIGENCE ────────────────────────────────────── */}
      <section className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-center" style={{ color: "#f97316" }}>Sector Intelligence System</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-black text-center mb-4 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              13-sector{" "}
              <span style={{ background: "linear-gradient(135deg, #8b5cf6, #6d28d9)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                composite scoring
              </span>
            </h2>
            <p className="text-center max-w-xl mx-auto mb-12" style={{ color: "var(--text-secondary)" }}>
              Every crypto sector scored on fundraising velocity, institutional momentum, and 30-day trend. Updated every 5 minutes with Redis caching.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-10">
            {sectorLoading ? (
              <ProductionState state="loading" className="col-span-full" />
            ) : !sectorIntel?.length ? (
              <ProductionState state="unavailable" className="col-span-full" title="SSI scores loading" message="Sector intelligence loads from /api/sectors/intel." />
            ) : sectorIntel.map((s, i) => {
              const color = verdictColor(s.verdict);
              return (
              <motion.div key={s.sectorName}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.04, duration: 0.4 }}>
                <div className="rounded-[10px] p-3" style={{ border: "1px solid var(--glass-border)", background: "var(--bg-card)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>{s.sectorName}</span>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{
                      background: color === "#22c55e" ? "rgba(34,197,94,0.12)" : color === "#ef4444" ? "rgba(239,68,68,0.12)" : color === "#60a5fa" ? "rgba(96,165,250,0.12)" : "rgba(249,115,22,0.12)",
                      color,
                    }}>{s.verdict.replace("_", " ")}</span>
                  </div>
                  <div className="text-xl font-black font-mono mb-1.5" style={{ color }}>{s.score}</div>
                  <div className="h-1.5 rounded-full" style={{ background: "var(--surface-2)" }}>
                    <motion.div
                      initial={{ width: 0 }} whileInView={{ width: `${s.score}%` }}
                      viewport={{ once: true }} transition={{ delay: 0.2 + i * 0.04, duration: 0.6, ease: [0.16, 1, 0.3, 1] as [number,number,number,number] }}
                      className="h-1.5 rounded-full" style={{ background: color }} />
                  </div>
                </div>
              </motion.div>
            );})}
          </div>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }}
            className="rounded-[12px] p-5 flex flex-col md:flex-row items-start md:items-center gap-4"
            style={{ border: "1px solid rgba(139,92,246,0.25)", background: "rgba(139,92,246,0.06)" }}>
            <div className="flex-1">
              <div className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>Score formula</div>
              <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                <span className="font-mono" style={{ color: "#8b5cf6" }}>score = (S1 × 0.30) + (S2 × 0.35) + (S3 × 0.35)</span>
                {" "}— S1: fundraising velocity · S2: institutional momentum · S3: 30-day trend
              </p>
            </div>
            <a href="/docs#ssi" className="text-xs font-bold px-3 py-1.5 rounded-[6px] flex-shrink-0"
              style={{ background: "rgba(139,92,246,0.15)", color: "#8b5cf6", border: "1px solid rgba(139,92,246,0.3)" }}>
              Full SSI docs →
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── 9. SIGNAL TRACK RECORD ────────────────────────────────────────── */}
      <section id="track-record" className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-center" style={{ color: "#f97316" }}>Track Record</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-black text-center mb-4 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Every signal,{" "}
              <span style={{ background: "linear-gradient(135deg, #22c55e, #16a34a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                tracked to outcome
              </span>
            </h2>
            <p className="text-center max-w-xl mx-auto mb-12" style={{ color: "var(--text-secondary)" }}>
              Signals are evaluated hourly against live prices. HIT when take-profit is reached, STOP when stop-loss fires, DRIFT after 72 hours without resolution.
            </p>
          </motion.div>
          {/* Live stats from /api/public/signals */}
          {(() => {
            const resolved = liveTrackRecord?.data ?? [];
            const meta = liveTrackRecord?.meta;
            const isLive = !!meta && meta.total > 0;

            if (trackRecordLoading) {
              return <ProductionState state="loading" compact className="mb-10" />;
            }

            if (!isLive) {
              return (
                <ProductionState
                  state="unavailable"
                  title="Track record loading"
                  message="Resolved signal outcomes will appear here once the backend has evaluated signals."
                  action={
                    <Link to="/track-record" className="text-sm font-semibold text-[var(--accent)]">
                      Open track record
                    </Link>
                  }
                  className="mb-10"
                />
              );
            }

            const total = meta!.total;
            const hits = meta!.hits ?? resolved.filter((s) => s.outcome === 'HIT').length;
            const stops = resolved.filter((s) => s.outcome === 'STOP').length;
            const drifts = resolved.filter((s) => s.outcome === 'DRIFT').length;
            const hitRate = meta!.hit_rate != null ? meta!.hit_rate : 0;
            const stopRate = total > 0 ? Math.round((stops / total) * 100) : 0;
            const driftRate = total > 0 ? Math.round((drifts / total) * 100) : 0;
            const pendingRate = Math.max(0, 100 - hitRate - stopRate - driftRate);
            return (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                  {[
                    { label: "HIT",     value: `${hitRate}%`,     sub: "Take-profit reached", color: "#22c55e", bg: "rgba(34,197,94,0.08)" },
                    { label: "STOP",    value: `${stopRate}%`,    sub: "Stop-loss triggered",  color: "#ef4444", bg: "rgba(239,68,68,0.08)"  },
                    { label: "DRIFT",   value: `${driftRate}%`,   sub: "Expired after 72h",    color: "#60a5fa", bg: "rgba(96,165,250,0.08)" },
                    { label: "PENDING", value: `${pendingRate}%`, sub: "Active, evaluating",   color: "#f97316", bg: "rgba(249,115,22,0.08)" },
                  ].map((stat, i) => (
                    <motion.div key={stat.label}
                      initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.4 }}>
                      <div className="rounded-[12px] p-5 text-center" style={{ background: stat.bg, border: `1px solid ${stat.color}33` }}>
                        <div className="text-3xl font-black font-mono mb-1" style={{ color: stat.color }}>{stat.value}</div>
                        <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: stat.color }}>{stat.label}</div>
                        <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{stat.sub}</div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                  className="rounded-[12px] overflow-hidden" style={{ border: "1px solid var(--glass-border)" }}>
                  <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--glass-border)", background: "var(--bg-card)" }}>
                    <span className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>
                      Recent signals {isLive ? <span className="ml-1 text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>({total} resolved)</span> : null}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: isLive ? "#22c55e" : "#f97316" }} />
                      <span className="text-[10px] font-mono" style={{ color: "#22c55e" }}>LIVE</span>
                    </div>
                  </div>
                  {resolved.slice(0, 6).map((sig, i) => (
                    <div key={sig.id ?? i} className="flex items-center gap-4 px-5 py-3 border-b" style={{ borderColor: "var(--glass-border)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                      <span className="text-sm font-bold w-10" style={{ color: "var(--text-primary)" }}>{sig.asset}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded w-14 text-center" style={{
                        background: sig.direction === "LONG" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                        color: sig.direction === "LONG" ? "#22c55e" : "#ef4444",
                      }}>{sig.direction?.toUpperCase()}</span>
                      <span className="text-[10px] font-mono w-8 text-right" style={{ color: "var(--text-muted)" }}>{sig.confidence}%</span>
                      <span className="text-[10px] font-mono flex-1" style={{ color: "var(--text-muted)" }}>
                        {sig.created_at ? new Date(sig.created_at).toLocaleDateString() : "—"}
                        {sig.outcome_price ? ` · Exit $${Number(sig.outcome_price).toLocaleString()}` : ""}
                      </span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{
                        background: sig.outcome === "HIT" ? "rgba(34,197,94,0.12)" : sig.outcome === "STOP" ? "rgba(239,68,68,0.12)" : sig.outcome === "PENDING" ? "rgba(249,115,22,0.12)" : "rgba(96,165,250,0.12)",
                        color: sig.outcome === "HIT" ? "#22c55e" : sig.outcome === "STOP" ? "#ef4444" : sig.outcome === "PENDING" ? "#f97316" : "#60a5fa",
                      }}>{sig.outcome ?? "ACTIVE"}</span>
                    </div>
                  ))}
                </motion.div>
              </>
            );
          })()}
        </div>
      </section>

      {/* ── 10. MACRO INTELLIGENCE ────────────────────────────────────────── */}
      <section className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "#f97316" }}>Macro Intelligence</p>
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] font-black mb-2 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>Regime Score Breakdown</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>Live risk-on/risk-off score from ETF flows, BTC momentum, and macro calendar proximity via /api/agents/macro.</p>
            <div className="rounded-[8px] border p-4" style={{ borderColor: "var(--glass-border)", background: "var(--bg-card)" }}>
              {macroLoading ? (
                <ProductionState state="loading" compact />
              ) : !macroOutlook ? (
                <ProductionState state="unavailable" compact title="Macro unavailable" />
              ) : mounted && macroChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={macroChartData}>
                  <defs>
                    <linearGradient id="inflowGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" />
                  <XAxis dataKey="name" tick={{ fill: "var(--text-muted)", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: "var(--text-muted)", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid var(--glass-border)", borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="score" stroke="#f97316" fill="url(#inflowGrad)" strokeWidth={2} dot={false} isAnimationActive />
                </AreaChart>
              </ResponsiveContainer>
              ) : null}
              {macroOutlook ? (
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span style={{ color: "var(--text-muted)" }}>Composite score</span>
                  <span className="font-black font-mono" style={{ color: "#f97316" }}>{macroOutlook.score}/100 · {macroOutlook.regime}</span>
                </div>
              ) : null}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "#f97316" }}>Macro Calendar</p>
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] font-black mb-6 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>Upcoming events</h2>
            <div className="flex flex-col gap-0">
              {macroLoading ? (
                <ProductionState state="loading" compact />
              ) : !macroOutlook?.upcomingEvents?.length ? (
                <ProductionState state="unavailable" compact title="No upcoming macro events" message="Calendar events load from SoSoValue macro API." />
              ) : macroOutlook.upcomingEvents.map((ev, i) => (
                <motion.div key={`${ev.name}-${i}`}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className="flex items-center gap-4 py-3 border-b"
                  style={{ borderColor: "var(--glass-border)" }}
                >
                  <span className="text-xs w-14 flex-shrink-0" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{formatMacroDate(ev.date)}</span>
                  <span className="text-xs px-2 py-0.5 rounded font-bold w-16 text-center flex-shrink-0 uppercase"
                    style={{ background: "rgba(249,115,22,0.1)", color: "#f97316" }}>{ev.importance}</span>
                  <span className="text-sm flex-1" style={{ color: "var(--text-primary)" }}>{ev.name}</span>
                  <ImpactBadge impact={ev.importance?.toUpperCase() === 'HIGH' ? 'HIGH' : ev.importance?.toUpperCase() === 'LOW' ? 'LOW' : 'MEDIUM'} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 9. DEX EXECUTION FLOW ─────────────────────────────────────────── */}
      <section className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-4xl mx-auto" ref={execRef}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-center" style={{ color: "#f97316" }}>DEX Execution</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-black text-center mb-4 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Trade in{" "}
              <span style={{
                background: "linear-gradient(135deg, #f97316, #ea580c)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>plain English</span>
            </h2>
            <p className="text-center max-w-xl mx-auto mb-16" style={{ color: "var(--text-secondary)" }}>Research → signal → risk gate → MetaMask EIP-712 sign → SoDEX mainnet relay → public outcome tracking.</p>
          </motion.div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-12">
            {EXECUTION_STEPS.map((step, i) => {
              const icons = [Brain, Zap, Shield, Wallet, Bolt, Target];
              const Icon = icons[i] ?? Check;
              const lit = execStep >= i;
              return (
                <div key={step.step} className="flex items-center gap-3 w-full md:w-auto">
                  <motion.div
                    animate={{ opacity: lit ? 1 : 0.3, scale: lit ? 1 : 0.95 }}
                    transition={{ duration: 0.4 }}
                    className="flex-1 md:flex-none p-4 rounded-[8px] border text-center"
                    style={{
                      minWidth: 120,
                      borderColor: lit ? "rgba(249,115,22,0.45)" : "var(--glass-border)",
                      background: lit ? "rgba(249,115,22,0.07)" : "var(--bg-card)",
                    }}
                  >
                    <div className="w-9 h-9 rounded-[8px] flex items-center justify-center mx-auto mb-2"
                      style={{ background: lit ? "rgba(249,115,22,0.15)" : "var(--surface-2)" }}>
                      <Icon className="w-4 h-4" style={{ color: lit ? "#f97316" : "var(--text-muted)" } as React.CSSProperties} />
                    </div>
                    <div className="font-bold text-xs mb-1" style={{ color: "var(--text-primary)" }}>{step.title}</div>
                    <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{step.body.slice(0, 48)}…</div>
                  </motion.div>
                  {i < EXECUTION_STEPS.length - 1 && (
                    <motion.div
                      animate={{ scaleX: execStep > i ? 1 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="hidden md:block h-px flex-1 origin-left"
                      style={{ background: "#f97316" }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="rounded-[8px] border overflow-hidden"
            style={{ borderColor: "var(--glass-border)", background: theme === "dark" ? "#0a0b10" : "#f8f8f8" }}
          >
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: "var(--glass-border)" }}>
              <div className="flex gap-1.5">{["#ef4444","#eab308","#22c55e"].map((c) => <div key={c} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />)}</div>
              <span className="text-xs ml-2" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>EIP-712 · SoDEX Order</span>
            </div>
            <pre className="p-5 text-xs overflow-x-auto" style={{ color: "#f97316", fontFamily: "var(--font-mono)", lineHeight: 1.7 }}>{`{
  "types": {
    "Order": [
      { "name": "maker",     "type": "address" },
      { "name": "asset",     "type": "string"  },
      { "name": "side",      "type": "string"  },
      { "name": "quantity",  "type": "uint256" },
      { "name": "stopLoss",  "type": "uint256" },
      { "name": "nonce",     "type": "uint256" }
    ]
  },
  "message": {
    "asset":    "BTC-USDT",
    "side":     "BUY",
    "quantity": "0.5",
    "stopLoss": "2.00%",
    "nonce":    "0x4a2f..."
  }
}`}</pre>
          </motion.div>
        </div>
      </section>

      {/* ── 10. TELEGRAM BOT + IDENTITY LINKING ──────────────────────────── */}
      <section id="bot" className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-center" style={{ color: "#0088cc" }}>Telegram Bot</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-black text-center mb-4 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Trade from{" "}
              <span style={{
                background: "linear-gradient(135deg, #0088cc, #0066aa)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>your phone</span>
            </h2>
            <p className="text-center max-w-xl mx-auto mb-16 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              The SoSoMind Telegram bot delivers real-time signals, portfolio updates, and DEX execution — no browser needed. Link your bot wallet to your dashboard in three steps.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                step: "01", title: "Start the Bot",
                desc: "Open @SosoMindbot on Telegram. A secure EVM wallet is auto-generated — no seed phrases, no setup.",
                icon: Send, color: "#0088cc",
              },
              {
                step: "02", title: "Connect Dashboard",
                desc: "Connect MetaMask on the web dashboard. Go to Profile and tap \"Generate Link Code\" to get a one-time code.",
                icon: Wallet, color: "#f97316",
              },
              {
                step: "03", title: "Run /link",
                desc: "Send /link <code> in the bot. Your Telegram identity permanently merges with your dashboard wallet.",
                icon: Check, color: "#22c55e",
              },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: i * 0.15, duration: 0.5 }}>
                  <SpotlightCard className="p-6 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-xs font-black font-mono" style={{ color: s.color, fontFamily: "var(--font-mono)" }}>{s.step}</span>
                      <div className="w-9 h-9 rounded-[8px] flex items-center justify-center"
                        style={{ background: s.color + "15", border: `1px solid ${s.color}30` }}>
                        <Icon className="w-4 h-4" style={{ color: s.color } as React.CSSProperties} />
                      </div>
                    </div>
                    <div className="font-bold mb-2 text-sm" style={{ color: "var(--text-primary)" }}>{s.title}</div>
                    <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{s.desc}</p>
                  </SpotlightCard>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center">
            <a href="https://t.me/SosoMindbot" target="_blank" rel="noopener noreferrer">
              <MagneticButton
                className="inline-flex items-center gap-2 px-8 py-4 rounded-[20px] font-bold text-white text-base"
                style={{ background: "linear-gradient(135deg,#0088cc 0%,#0066aa 100%)" }}
              >
                <Send className="w-5 h-5" /> Start SoSoMind Bot
              </MagneticButton>
            </a>
          </div>
        </div>
      </section>

      {/* ── 11. LEADERBOARD + MARKETPLACE ────────────────────────────────── */}
      <section className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "#f97316" }}>Community</p>
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] font-black mb-6 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>Trader Leaderboard</h2>
            <div className="flex flex-col gap-3 mb-8">
              {paperLeaderboard === null ? (
                <ProductionState state="loading" compact />
              ) : paperLeaderboard.length === 0 ? (
                <ProductionState
                  state="empty"
                  title="No leaderboard entries yet"
                  message="Paper trading rankings appear once traders publish results."
                  compact
                />
              ) : (
                paperLeaderboard.map((t, i) => (
                <motion.div key={t.user_id ?? t.name ?? i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.45 }}
                >
                  <SpotlightCard className="p-4 flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-sm"
                      style={{
                        background: i === 0 ? "rgba(234,179,8,0.15)" : i === 1 ? "rgba(148,163,184,0.15)" : "rgba(180,123,89,0.15)",
                        color: i === 0 ? "#eab308" : i === 1 ? "#94a3b8" : "#b47b59",
                        border: "1px solid currentColor",
                      }}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                        {t.display_name ?? t.user_id ?? 'Trader'}
                      </div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {t.trades ?? t.signals ?? 0} trades
                        {t.win_rate != null ? ` · ${(Number(t.win_rate) * 100).toFixed(0)}% win rate` : ''}
                      </div>
                    </div>
                    <span className="font-black text-sm" style={{ color: t.total_pnl_usd >= 0 ? "#22c55e" : "#ef4444" }}>
                      {t.total_pnl_usd != null
                        ? `${t.total_pnl_usd >= 0 ? '+' : ''}$${Math.abs(Number(t.total_pnl_usd)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                        : '—'}
                    </span>
                  </SpotlightCard>
                </motion.div>
              )))}
            </div>
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--accent)] mb-8"
            >
              View full leaderboard <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "#f97316" }}>Marketplace</p>
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] font-black mb-6 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>Signal Strategies</h2>
            {marketLeaderboard === null ? (
              <ProductionState state="loading" compact />
            ) : marketLeaderboard.length === 0 ? (
              <ProductionState
                state="empty"
                title="Marketplace strategies unavailable"
                message="Published strategies will appear here when creators share verified track records."
                compact
              />
            ) : (
              marketLeaderboard.map((s, i) => (
              <motion.div key={s.user_id ?? s.display_name ?? i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.45 }}
                className="mb-3"
              >
                <SpotlightCard className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                        {s.display_name ?? s.user_id ?? 'Strategy'}
                      </div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {s.followers != null ? `${s.followers} followers` : 'Community strategy'}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-6 text-xs" style={{ color: "var(--text-muted)" }}>
                    {s.win_rate_published != null || s.winRate != null ? (
                      <span>
                        Win rate{' '}
                        <strong style={{ color: "#22c55e" }}>
                          {((Number(s.win_rate_published ?? s.winRate) * 100) || 0).toFixed(0)}%
                        </strong>
                      </span>
                    ) : null}
                    {s.total_pnl_usd != null ? (
                      <span>PnL ${Number(s.total_pnl_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    ) : null}
                  </div>
                </SpotlightCard>
              </motion.div>
            )))}
          </motion.div>
        </div>
      </section>

      {/* ── 11. DATA SOURCES ──────────────────────────────────────────────── */}
      <section id="integrations" className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-center" style={{ color: "#f97316" }}>Data Sources</p>
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-black text-center mb-4 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              13 live{" "}
              <span style={{
                background: "linear-gradient(135deg, #8b5cf6, #6366f1)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>data streams</span>
            </h2>
            <p className="text-center max-w-xl mx-auto mb-12" style={{ color: "var(--text-secondary)" }}>Every agent is powered by real-time data from the most comprehensive crypto intelligence network.</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {DATA_SOURCES.map((ds, i) => {
              const Icon = ds.icon;
              const isMCP = ds.type === "MCP";
              return (
                <motion.div key={ds.name}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ delay: (i % 5) * 0.07, duration: 0.45 }}
                >
                  <SpotlightCard className="p-4 text-center h-full flex flex-col items-center justify-center gap-2">
                    <div className="w-9 h-9 rounded-[8px] flex items-center justify-center"
                      style={{
                        background: isMCP ? "rgba(139,92,246,0.12)" : "rgba(249,115,22,0.1)",
                        border: isMCP ? "1px solid rgba(139,92,246,0.3)" : "1px solid rgba(249,115,22,0.2)",
                      }}>
                      {isMCP
                        ? <MCP size={16} style={{ color: "#8b5cf6" }} />
                        : <Icon className="w-4 h-4" style={{ color: "#f97316" } as React.CSSProperties} />}
                    </div>
                    <div className="font-bold text-xs" style={{ color: "var(--text-primary)" }}>{ds.name}</div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] px-1.5 py-0.5 rounded"
                        style={{
                          background: isMCP ? "rgba(139,92,246,0.1)" : "rgba(249,115,22,0.08)",
                          color: isMCP ? "#8b5cf6" : "#f97316",
                          fontFamily: "var(--font-mono)",
                        }}>{ds.type}</span>
                      <span className="text-[10px]" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{ds.tools}t</span>
                    </div>
                  </SpotlightCard>
                </motion.div>
              );
            })}
          </div>

          {/* AI Models powering the agents */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: 0.2, duration: 0.5 }}
            className="mt-12 pt-10 border-t" style={{ borderColor: "var(--glass-border)" }}
          >
            <p className="text-xs font-bold uppercase tracking-widest mb-6 text-center" style={{ color: "var(--text-muted)" }}>AI Provider Fallback Chain</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {[
                "Cerebras",
                "SambaNova",
                "Together AI",
                "OpenRouter",
                "Groq",
                "Gemini",
              ].map((label) => (
                <div key={label} className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] border"
                  style={{ borderColor: "var(--glass-border)", background: "var(--bg-card)" }}>
                  <Cpu className="w-4 h-4" style={{ color: "#f97316" }} />
                  <span className="text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>{label}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-xs mt-4" style={{ color: "var(--text-muted)" }}>Research synthesis uses the first available provider when API keys are configured.</p>
          </motion.div>
        </div>
      </section>

      {/* ── 12. FAQ ───────────────────────────────────────────────────────── */}
      <section id="faq" className="py-24 px-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-4 text-center" style={{ color: "#f97316" }}>FAQ</p>
            <h2 className="text-[clamp(2rem,5vw,3rem)] font-black text-center mb-12 tracking-tight" style={{ fontFamily: "var(--font-display)" }}>Common questions</h2>
          </motion.div>
          <div>
            {FAQ_ITEMS.map((item, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="border-b" style={{ borderColor: "var(--glass-border)" }}
              >
                <button className="w-full flex items-center justify-between py-5 text-left gap-4"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{item.q}</span>
                  <motion.div animate={{ rotate: openFaq === i ? 180 : 0 }} transition={{ duration: 0.25 }}>
                    <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="pb-5 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 13. FINAL CTA + FOOTER ────────────────────────────────────────── */}
      <section className="py-32 px-6 border-t relative overflow-hidden" style={{ borderColor: "var(--glass-border)" }}>
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 70%)" }}
        />
        <div className="max-w-2xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}>
            <motion.h2
              initial={{ opacity: 0, y: 30, filter: "blur(12px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] as [number,number,number,number] }}
              className="text-[clamp(2.5rem,6vw,5rem)] font-black mb-4 tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Start trading<br />
              <span style={{
                background: "linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>smarter today.</span>
            </motion.h2>
            <p className="text-lg mb-10" style={{ color: "var(--text-secondary)" }}>
              Connect MetaMask and trade on SoDEX Mainnet with live intelligence, risk preflight, and public outcome tracking.
            </p>
            {address ? (
              <Link to="/dashboard">
                <MagneticButton
                  className="inline-flex items-center gap-3 px-10 py-5 rounded-[20px] font-bold text-white text-lg"
                  style={{ background: "linear-gradient(135deg,#f97316 0%,#ea580c 50%,#c2410c 100%)" }}
                >
                  Open Dashboard <ArrowRight className="w-5 h-5" />
                </MagneticButton>
              </Link>
            ) : (
              <MagneticButton
                onClick={connect}
                disabled={isConnecting}
                className="inline-flex items-center gap-3 px-10 py-5 rounded-[20px] font-bold text-white text-lg disabled:opacity-60"
                style={{ background: "linear-gradient(135deg,#f97316 0%,#ea580c 50%,#c2410c 100%)" }}
              >
                <Wallet className="w-5 h-5" />
                {isConnecting ? "Connecting…" : "Connect Wallet"}
              </MagneticButton>
            )}
            <motion.div
              initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-8 flex items-center justify-center gap-2"
            >
              <span className="w-2 h-2 rounded-full" style={{ background: "#22c55e" }} />
              <span className="text-sm" style={{ color: "var(--text-muted)" }}>SoDEX Mainnet · chainId 286623 · Testnet available in Settings</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t py-16 px-6" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <LogoMark size={30} />
              <span
                className="font-black text-xl tracking-tight"
                style={{
                  fontFamily: 'var(--font-display)',
                  letterSpacing: '-0.04em',
                  color: theme === 'dark' ? '#f1f1f5' : '#111111',
                }}
              >
                SoSo<span style={{ color: '#f97316' }}>Mind</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Production agentic trading on SoDEX Mainnet with live SoSoValue intelligence and non-custodial EIP-712 execution.
            </p>
            <div className="flex gap-3 mt-5">
              {[
                { Icon: X,     href: "#",                              label: "X / Twitter"    },
                { Icon: Code2, href: "#",                              label: "GitHub"          },
                { Icon: Send,  href: "https://t.me/SosoMindbot",      label: "Telegram Bot"    },
              ].map(({ Icon, href, label }) => (
                <a key={label} href={href}
                  target={href.startsWith("https") ? "_blank" : undefined}
                  rel={href.startsWith("https") ? "noopener noreferrer" : undefined}
                  aria-label={label}
                  className="w-9 h-9 flex items-center justify-center rounded-full border transition-colors"
                  style={{ borderColor: "var(--glass-border)", color: "var(--text-muted)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = label === "Telegram Bot" ? "#0088cc" : "#f97316"; (e.currentTarget as HTMLAnchorElement).style.borderColor = label === "Telegram Bot" ? "rgba(0,136,204,0.4)" : "rgba(249,115,22,0.4)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-muted)"; (e.currentTarget as HTMLAnchorElement).style.borderColor = "var(--glass-border)"; }}>
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
          {[
            { title: "Product", links: FOOTER_LINKS.product },
            { title: "Resources", links: FOOTER_LINKS.resources },
            { title: "Legal", links: FOOTER_LINKS.legal },
          ].map((col) => (
            <div key={col.title}>
              <div className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "var(--text-muted)" }}>{col.title}</div>
              <ul className="flex flex-col gap-2.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith('http') ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-sm transition-colors" style={{ color: "var(--text-secondary)" }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-primary)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-secondary)"; }}>
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.href} className="text-sm transition-colors" style={{ color: "var(--text-secondary)" }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-primary)"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "var(--text-secondary)"; }}>
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="max-w-5xl mx-auto mt-12 pt-6 border-t flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderColor: "var(--glass-border)" }}>
          <span className="text-sm" style={{ color: "var(--text-muted)" }}>© {new Date().getFullYear()} SoSoMind. Built for serious traders.</span>
          <span className="text-xs" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>v2.0.0 · Powered by SoSoValue + SoDEX</span>
        </div>
      </footer>

      <style>{`
        @keyframes gridFloat {
          0%   { background-position: 0 0; }
          100% { background-position: 28px 28px; }
        }
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes beam-pulse {
          0%, 100% { opacity: 0.25; transform: scaleX(0.85); }
          50%       { opacity: 1;    transform: scaleX(1); }
        }
        @keyframes ping-ring {
          0%   { transform: scale(1);    opacity: 0.5; }
          75%  { transform: scale(1.35); opacity: 0; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        :focus-visible {
          outline: 2px solid #f97316;
          outline-offset: 3px;
          border-radius: 4px;
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
}
